import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiUsers, FiFileText, FiSearch, FiUserCheck, FiShield, FiClock, FiCheckCircle } = FiIcons;

const RecruitmentWorkflow = () => {
  const [selectedStage, setSelectedStage] = useState('planning');

  const recruitmentStages = {
    planning: {
      title: 'Job Analysis & Planning',
      subtitle: 'Foundation for compliant recruitment',
      timeline: '1-2 weeks',
      stages: [
        {
          stage: 'Job Analysis & Role Definition',
          urgency: 'high',
          icon: FiSearch,
          actions: [
            '**Conduct job analysis** - duties, responsibilities, reporting lines',
            'Define essential vs desirable criteria (avoid discrimination)',
            'Determine salary benchmarking and grading',
            'Assess genuine occupational requirements (GOR)',
            'Review existing job descriptions for currency'
          ],
          compliance: [
            { type: 'legal', text: 'Equality Act 2010: Job requirements must be proportionate and non-discriminatory' },
            { type: 'acas', text: 'Clear job analysis reduces recruitment disputes' }
          ]
        },
        {
          stage: 'Documentation Preparation',
          urgency: 'high',
          icon: FiFileText,
          actions: [
            'Draft **job description** with key accountabilities',
            'Create **person specification** (essential/desirable criteria)',
            'Prepare **recruitment policy** compliance checklist',
            'Design application form (avoid prohibited questions)',
            'Set **interview framework** and competency questions'
          ],
          compliance: [
            { type: 'legal', text: 'Application forms must not request protected characteristic information' },
            { type: 'acas', text: 'Structured interviews reduce bias and improve selection' }
          ]
        }
      ]
    },
    advertising: {
      title: 'Job Advertising & Sourcing',
      subtitle: 'Attracting diverse, qualified candidates',
      timeline: '2-4 weeks',
      stages: [
        {
          stage: 'Advertisement Creation',
          urgency: 'medium',
          icon: FiUsers,
          actions: [
            'Write inclusive job advertisement (avoid biased language)',
            'Include **essential information**: salary, location, hours',
            'Add equality statement and disability confident logo',
            'Specify application method and closing date',
            'Include **right to work** requirements clearly'
          ],
          compliance: [
            { type: 'legal', text: 'Advertisements must not discriminate directly or indirectly' },
            { type: 'legal', text: 'Must include right to work requirements (Immigration Act 2016)' }
          ]
        },
        {
          stage: 'Multi-Channel Advertising',
          urgency: 'medium',
          icon: FiSearch,
          actions: [
            'Post on diverse job boards and company website',
            'Use inclusive recruitment channels',
            'Consider **positive action** measures if underrepresentation exists',
            'Set reasonable application deadline (minimum 2 weeks)',
            'Monitor application demographics for bias indicators'
          ],
          compliance: [
            { type: 'legal', text: 'Positive action permitted under Equality Act 2010 s159' },
            { type: 'acas', text: 'Diverse sourcing improves candidate quality and reduces bias' }
          ]
        }
      ]
    },
    selection: {
      title: 'Selection & Assessment',
      subtitle: 'Fair and objective candidate evaluation',
      timeline: '3-5 weeks',
      stages: [
        {
          stage: 'Application Screening',
          urgency: 'high',
          icon: FiFileText,
          actions: [
            '**Shortlist against person specification** criteria only',
            'Use structured scoring matrix (avoid unconscious bias)',
            'Document reasons for rejection at each stage',
            'Ensure consistent application of criteria',
            '**Right to work preliminary check** (document verification)'
          ],
          compliance: [
            { type: 'legal', text: 'Selection decisions must be based on job-related criteria only' },
            { type: 'legal', text: 'Right to work checks required before employment starts' }
          ]
        },
        {
          stage: 'Interview Process',
          urgency: 'critical',
          icon: FiUsers,
          actions: [
            'Conduct **structured competency-based interviews**',
            'Use diverse interview panel (gender, ethnicity where possible)',
            'Ask consistent questions to all candidates',
            'Avoid questions about protected characteristics',
            '**Document interview scores** and rationale for decisions'
          ],
          compliance: [
            { type: 'legal', text: 'Interview questions must be job-relevant and non-discriminatory' },
            { type: 'acas', text: 'Structured interviews with scoring improve fairness and defensibility' }
          ]
        },
        {
          stage: 'Assessment & Testing',
          urgency: 'medium',
          icon: FiCheckCircle,
          actions: [
            'Conduct **job-relevant skills assessments** only',
            'Ensure tests are validated and bias-free',
            'Provide reasonable adjustments for disabled candidates',
            'Document assessment criteria and scoring',
            'Maintain confidentiality of assessment results'
          ],
          compliance: [
            { type: 'legal', text: 'Reasonable adjustments duty under Equality Act 2010' },
            { type: 'legal', text: 'Assessments must be proportionate and job-relevant' }
          ]
        }
      ]
    },
    checks: {
      title: 'Pre-Employment Checks',
      subtitle: 'Essential verification before employment',
      timeline: '2-6 weeks',
      stages: [
        {
          stage: 'Right to Work Verification',
          urgency: 'critical',
          icon: FiShield,
          actions: [
            '**Conduct right to work checks** before employment starts',
            'Accept original documents only (List A or List B)',
            'Take and retain clear copies of documents',
            'Record date of check and follow-up dates if required',
            'Use **Employer Checking Service** for digital status verification'
          ],
          compliance: [
            { type: 'legal', text: 'Legal requirement under Immigration, Asylum and Nationality Act 2006' },
            { type: 'legal', text: 'Failure to conduct proper checks can result in civil penalty up to £20,000' }
          ]
        },
        {
          stage: 'DBS and Background Checks',
          urgency: 'critical',
          icon: FiFileText,
          actions: [
            'Determine **DBS check level** required (Basic/Standard/Enhanced)',
            'Only request DBS if role involves regulated activity',
            'Process DBS application through registered body',
            'Conduct **risk assessment** if criminal record disclosed',
            'Follow **DBS Code of Practice** throughout process'
          ],
          compliance: [
            { type: 'legal', text: 'DBS checks only permitted for eligible positions (Police Act 1997)' },
            { type: 'legal', text: 'Must follow Rehabilitation of Offenders Act 1974 guidance' }
          ]
        },
        {
          stage: 'References and Verification',
          urgency: 'high',
          icon: FiUserCheck,
          actions: [
            'Obtain **minimum two references** (including most recent employer)',
            'Verify employment history and reason for leaving',
            'Check academic/professional qualifications if job-relevant',
            'Conduct **occupational health assessment** if required',
            'Document all verification outcomes'
          ],
          compliance: [
            { type: 'acas', text: 'Reference checking helps verify candidate suitability' },
            { type: 'legal', text: 'Health assessments must comply with Equality Act 2010' }
          ]
        }
      ]
    },
    offer: {
      title: 'Job Offer & Contract',
      subtitle: 'Formal offer and contract preparation',
      timeline: '1-2 weeks',
      stages: [
        {
          stage: 'Conditional Job Offer',
          urgency: 'high',
          icon: FiFileText,
          actions: [
            'Make **conditional offer** subject to satisfactory checks',
            'Clearly state conditions (references, DBS, right to work)',
            'Include salary, start date, and key terms',
            'Set deadline for acceptance (reasonable timeframe)',
            'Provide **written offer letter** with all conditions'
          ],
          compliance: [
            { type: 'legal', text: 'Conditional offers protect employer until all checks completed' },
            { type: 'acas', text: 'Clear offer terms reduce misunderstandings' }
          ]
        },
        {
          stage: 'Contract Preparation',
          urgency: 'critical',
          icon: FiUsers,
          actions: [
            'Prepare **written statement of employment** (within 2 months)',
            'Include all mandatory terms: pay, hours, holidays, notice',
            'Add role-specific terms: probation, confidentiality, restraints',
            'Ensure contract complies with **Working Time Regulations**',
            'Include **auto-enrolment pension** provisions'
          ],
          compliance: [
            { type: 'legal', text: 'Written statement required by Employment Rights Act 1996 s1' },
            { type: 'legal', text: 'Auto-enrolment pension duty under Pensions Act 2008' }
          ]
        },
        {
          stage: 'Final Confirmation',
          urgency: 'critical',
          icon: FiCheckCircle,
          actions: [
            '**Confirm all pre-employment checks** satisfactory',
            'Convert conditional offer to **unconditional employment**',
            'Send final contract and employee handbook',
            'Arrange start date and induction schedule',
            'Notify unsuccessful candidates with feedback'
          ],
          compliance: [
            { type: 'legal', text: 'Employment only commences when all conditions satisfied' },
            { type: 'acas', text: 'Providing feedback improves employer brand' }
          ]
        }
      ]
    }
  };

  const currentStage = recruitmentStages[selectedStage];

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-600';
      case 'medium': return 'bg-yellow-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Recruitment / Pre-Employment Workflow</h1>
          <p className="text-gray-600">Comprehensive recruitment process ensuring legal compliance and best practices</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.keys(recruitmentStages).map((stage) => (
            <button
              key={stage}
              onClick={() => setSelectedStage(stage)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                selectedStage === stage 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {stage.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentStage.title}</h2>
            <p className="text-gray-600">{currentStage.subtitle}</p>
          </div>
          <div className="text-right">
            <span className="text-sm text-blue-600 font-medium">Timeline</span>
            <p className="text-lg font-bold text-blue-800">{currentStage.timeline}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {currentStage.stages.map((stage, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`${getUrgencyColor(stage.urgency)} px-6 py-4`}>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={stage.icon} className="text-white text-2xl" />
                <div>
                  <h3 className="text-white font-semibold text-xl">{stage.stage}</h3>
                  <span className="text-white opacity-90 text-sm capitalize">
                    {stage.urgency} Priority
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 mr-2" />
                    Required Actions
                  </h4>
                  <ul className="space-y-3">
                    {stage.actions.map((action, actionIndex) => (
                      <li key={actionIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                        <span 
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{ 
                            __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiShield} className="text-orange-600 mr-2" />
                    Compliance Requirements
                  </h4>
                  <div className="space-y-3">
                    {stage.compliance.map((requirement, reqIndex) => (
                      <div key={reqIndex} className="flex items-start space-x-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                          requirement.type === 'legal' 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-green-600 bg-green-50'
                        }`}>
                          {requirement.type === 'legal' ? '⚖️ Legal' : '🟢 ACAS'}
                        </span>
                        <span className="text-gray-700 text-sm">{requirement.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-semibold text-red-900 mb-2">⚖️ Critical Legal Requirements</h4>
          <ul className="text-sm text-red-800 space-y-1">
            <li>• Right to work checks (mandatory)</li>
            <li>• Non-discriminatory selection</li>
            <li>• DBS checks (if eligible roles only)</li>
            <li>• Written employment terms</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-2">🟢 ACAS Best Practices</h4>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• Structured interview processes</li>
            <li>• Diverse recruitment panels</li>
            <li>• Comprehensive reference checking</li>
            <li>• Candidate feedback provision</li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">📋 Documentation Required</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Selection decision rationale</li>
            <li>• Right to work evidence</li>
            <li>• DBS certificates (if applicable)</li>
            <li>• Reference verification</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentWorkflow;